import { useState } from 'react';
import { subMonths, endOfDay, startOfDay } from 'date-fns';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatCurrencyShort } from '@/lib/utils';
import { DateRangePicker } from '@/components/shared/date-range-picker';
import { StatCard } from '@/components/shared/stat-card';

const COLORS = [
  'hsl(152, 69%, 40%)',
  'hsl(221, 83%, 53%)',
  'hsl(38, 92%, 50%)',
  'hsl(0, 72%, 51%)',
  'hsl(280, 68%, 50%)',
  'hsl(180, 60%, 45%)',
  'hsl(330, 65%, 50%)',
  'hsl(200, 70%, 50%)',
  'hsl(60, 70%, 45%)',
];

const tooltipStyle = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
};

const axisProps = {
  stroke: 'hsl(var(--muted-foreground))',
  fontSize: 12,
  tickLine: false as const,
  axisLine: false as const,
};

interface TravelCustomerAnalyticsTabProps {
  customerId: string;
}

export function TravelCustomerAnalyticsTab({ customerId }: TravelCustomerAnalyticsTabProps) {
  const [dateRange, setDateRange] = useState({
    from: startOfDay(subMonths(new Date(), 12)),
    to: endOfDay(new Date()),
  });

  const queryInput = { customerId, startDate: dateRange.from, endDate: dateRange.to };

  const { data: stats, isLoading: statsLoading } = trpc.customerAnalytics.stats.useQuery(queryInput);
  const { data: revenueOverTime } = trpc.customerAnalytics.revenueOverTime.useQuery(queryInput);
  const { data: bookingsByStatus } = trpc.customerAnalytics.bookingsByStatus.useQuery(queryInput);
  const { data: serviceTypeBreakdown } = trpc.customerAnalytics.serviceTypeBreakdown.useQuery(queryInput);
  const { data: paymentTimeline } = trpc.customerAnalytics.paymentTimeline.useQuery(queryInput);
  const { data: spendingByPackage } = trpc.customerAnalytics.spendingByPackage.useQuery(queryInput);
  const { data: invoiceAging } = trpc.customerAnalytics.invoiceAging.useQuery({ customerId });

  if (statsLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const renderDonutLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
  }: {
    cx: number;
    cy: number;
    midAngle: number;
    outerRadius: number;
    percent: number;
  }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="hsl(var(--foreground))"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const agingData = invoiceAging
    ? [
        { bucket: 'Current', amount: invoiceAging.current, color: COLORS[0] },
        { bucket: '1-30 days', amount: invoiceAging.days30, color: COLORS[1] },
        { bucket: '31-60 days', amount: invoiceAging.days60, color: COLORS[2] },
        { bucket: '61-90 days', amount: invoiceAging.days90, color: COLORS[3] },
        { bucket: '90+ days', amount: invoiceAging.over90, color: COLORS[4] },
      ].filter((d) => d.amount > 0)
    : [];

  return (
    <div className="space-y-6">
      {/* Date Range Picker */}
      <div className="flex justify-end">
        <DateRangePicker value={dateRange} onChange={setDateRange} />
      </div>

      {/* Stat Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Revenue"
          value={stats?.revenue.value ?? 0}
          format="currency"
          change={stats?.revenue.change}
        />
        <StatCard
          title="Total Bookings"
          value={stats?.bookings.value ?? 0}
          format="number"
          change={stats?.bookings.change}
        />
        <StatCard
          title="Avg Booking Value"
          value={stats?.avgBookingValue.value ?? 0}
          format="currency"
          change={stats?.avgBookingValue.change}
        />
        <StatCard
          title="Outstanding"
          value={stats?.outstandingBalance ?? 0}
          format="currency"
        />
      </div>

      {/* Revenue Over Time & Bookings Over Time */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">Revenue Over Time</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={revenueOverTime ?? []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="analyticsRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(152, 69%, 40%)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(152, 69%, 40%)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis
                {...axisProps}
                tickFormatter={(value) => formatCurrencyShort(value)}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [formatCurrency(value), 'Revenue']}
              />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="hsl(152, 69%, 40%)"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#analyticsRevenueGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">Bookings Over Time</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={revenueOverTime ?? []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [value, 'Bookings']}
              />
              <Bar dataKey="bookings" fill="hsl(221, 83%, 53%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Booking Status & Service Type Donut Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">Bookings by Status</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={bookingsByStatus ?? []}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                label={renderDonutLabel}
              >
                {(bookingsByStatus ?? []).map((_, index) => (
                  <Cell key={`status-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">Service Type Breakdown</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={serviceTypeBreakdown ?? []}
                dataKey="amount"
                nameKey="type"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                label={renderDonutLabel}
              >
                {(serviceTypeBreakdown ?? []).map((_, index) => (
                  <Cell key={`service-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [formatCurrency(value), 'Revenue']}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Payment Timeline & Spending by Package */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">Payment Timeline</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={paymentTimeline ?? []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis
                {...axisProps}
                tickFormatter={(value) => formatCurrencyShort(value)}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [formatCurrency(value), 'Payments']}
              />
              <Bar dataKey="amount" fill="hsl(152, 69%, 40%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6">
          <h3 className="mb-4 font-semibold">Spending by Package</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={spendingByPackage ?? []}
              layout="vertical"
              margin={{ top: 10, right: 10, left: 80, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                type="number"
                {...axisProps}
                tickFormatter={(value) => formatCurrencyShort(value)}
              />
              <YAxis
                type="category"
                dataKey="name"
                {...axisProps}
                width={70}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [formatCurrency(value), 'Spent']}
              />
              <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
                {(spendingByPackage ?? []).map((_, index) => (
                  <Cell key={`pkg-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Invoice Aging */}
      <div className="border border-border bg-card p-6">
        <h3 className="mb-4 font-semibold">Invoice Aging</h3>
        {agingData.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={agingData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="bucket" {...axisProps} />
              <YAxis {...axisProps} tickFormatter={(value) => formatCurrencyShort(value)} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: number) => [formatCurrency(value), 'Outstanding']}
              />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                {agingData.map((entry, index) => (
                  <Cell key={`aging-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground">No outstanding invoices.</p>
        )}
      </div>
    </div>
  );
}
